import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {get, isEmpty, sortBy, trim} from "lodash";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {history} from "common/history";
import {PAGE_HEIGHT_WITH_NAVBAR, userTypes} from "common/helpers";
import {loginType} from "common/loginType";
import {color, font, space, type} from "common/tokens";
import useBusy from "common/useBusy";
import MentorMenteeSelector from "components/MentorMenteeSelector";
import MTMarkdown from "components/MTMarkdown";
import Question from 'components/Question/index';
import CtaButton from "components/redesign/CtaButton";
import questionFields from "components/redesign/questionFields";

// Laid out like a session step and a survey stage: the step name and its
// progress sit in a bar pinned to the top, the questions scroll under it, and
// the way on stays on a footer that never moves.
const useStyles = makeStyles({
  page: {
    background: color.SURFACE,
    height: PAGE_HEIGHT_WITH_NAVBAR,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '0 32px',
    boxSizing: 'border-box',
  },
  column: {
    width: 638,
    maxWidth: '100%',
    margin: '0 auto',
  },
  topBar: {
    flexShrink: 0,
    background: color.SURFACE,
    paddingBottom: 18,
  },
  eyebrow: {
    ...type.eyebrow,
    color: color.TEXT_MUTED,
    margin: '14px 0 0',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 0 16px',
  },
  back: {
    ...type.body,
    fontSize: 18,
    lineHeight: '26px',
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    gap: 10,
    flex: '1 1 0',
    minWidth: 0,
    cursor: 'pointer',
  },
  backArrow: {
    color: color.TEXT_MUTED,
    fontSize: 20,
    lineHeight: 1,
    flexShrink: 0,
  },
  progressTrack: {
    height: 4,
    borderRadius: 4,
    background: color.DIVIDER,
    overflow: 'hidden',
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
    background: color.ACCENT,
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowAnchor: 'none',
  },
  content: {
    padding: '24px 0 32px',
  },
  questions: questionFields,
  declaration: {
    ...type.body,
    color: color.TEXT_MUTED,
  },
  error: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.DANGER,
    margin: '0 0 16px',
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${color.DIVIDER}`,
  },
  footerInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
  },
  spacer: {
    flex: 1,
  },
  form: {
    display: 'contents',
  },
});

// The API wraps a field error as `[ErrorDetail(string='…', code='invalid')]`.
const readableError = message => (message || '')
  .replace(/^\[?ErrorDetail\(string='(.+?)', code='invalid'\)]?$/, '$1')
  .replace(/^\[|]$/g, '');

const projectsForEmailDomain = (projectList, emailDomain) => {
  if (!Array.isArray(projectList) || projectList.length === 0) return [];

  const matching = projectList
    .filter(project => (project.emailDomains || []).includes(emailDomain))
    .map(project => project.key);

  // A domain that matches a project narrows the list to that project; otherwise
  // every domain-restricted project drops out.
  return projectList.filter(project => matching.length
    ? matching.includes(project.key)
    : isEmpty(project.emailDomains));
};

const Signup = ({
  profile,
  signupPageIndex,
  addFormDetailsToProfile,
  gettingDeclaration,
  getProjectList,
  submitProfileData,
  supportedLanguages,
  initLanguages,
  auth,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(signupPageIndex);
  const [busy, run] = useBusy();
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEmpty(supportedLanguages)) initLanguages();
  }, []);

  // The step scrolls, so the first thing to fix is brought into view — after
  // the errors have painted, since the fields are only marked invalid then.
  useEffect(() => {
    if (isEmpty(errors)) return;
    const first = document.querySelector('[aria-invalid="true"]');
    if (first) {
      first.scrollIntoView({block: 'center', behavior: 'smooth'});
      first.focus({preventScroll: true});
    }
  }, [errors]);

  const scrollerRef = useRef(null);
  useLayoutEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [currentIndex]);

  const isMentor = () => userTypes.MENTOR === profile.userType;
  const userEmail = auth.provider === loginType.GOOGLE ? profile.email : auth.email;
  const userEmailDomain = !isEmpty(userEmail) && userEmail.split("@")[1];

  const handleChange = name => value => {
    addFormDetailsToProfile({[name]: value});
    setErrors(current => ({...current, [name]: undefined}));

    if (name === "phoneNumber" && profile.signupError) {
      addFormDetailsToProfile({signupError: false, signupErrorMessage: ""});
    }
  };

  // Submitting the step empty used to reach the API, which rejected the payload
  // and returned one generic message for the whole form. The fields are checked
  // here so each one says what is missing.
  const validateDetails = () => {
    const found = {};

    if (isEmpty(trim(profile.firstName))) found.firstName = t('firstNameRequired');
    if (isEmpty(trim(profile.lastName))) found.lastName = t('lastNameRequired');
    if (isMentor()) {
      if (isEmpty(get(profile, 'program.key'))) found.program = t('programRequired');
    } else if (isEmpty(trim(profile.institutionCode))) {
      found.institutionCode = t('institutionCodeRequired');
    }
    if (isEmpty(get(profile, 'primaryLanguage.key'))) found.primaryLanguage = t('primaryLanguageRequired');
    if (isEmpty(trim(profile.phoneNumber))) found.phoneNumber = t('phoneNumberRequired');

    setErrors(found);
    return isEmpty(found);
  };

  const submitDetails = () => {
    if (!validateDetails()) return;
    run(() => submitProfileData());
  };

  const steps = [
    {
      title: t('signUpMenteeMentorPageTitle'),
      cta: t('continueCaps'),
      disabled: isEmpty(profile.userType),
      onContinue: () => {
        run(() => gettingDeclaration());
        setCurrentIndex(1);
      },
      content: (
        <MentorMenteeSelector userType={profile.userType} onAnswered={addFormDetailsToProfile}/>
      ),
    },
    {
      title: t('signUpDeclarationPageTitle'),
      cta: t('iAccept'),
      onContinue: () => {
        if (isMentor()) run(() => getProjectList());
        setCurrentIndex(2);
      },
      content: <MTMarkdown className={classes.declaration}>{profile.declaration}</MTMarkdown>,
    },
    {
      title: t('pleaseFillDetails'),
      cta: t('continueCaps'),
      onContinue: submitDetails,
      content: (
        <div className={classes.questions}>
          <Question
            question={{
              label: t('signUpFirstNameLabel'),
              questionType: 'Text',
              autocomplete: 'given-name',
              mandatory: true,
            }}
            onAnswered={handleChange("firstName")}
            value={profile.firstName}
            error={errors.firstName}
          />
          <Question
            question={{
              label: t('signUpLastNameLabel'),
              questionType: 'Text',
              autocomplete: 'family-name',
              mandatory: true,
            }}
            onAnswered={handleChange("lastName")}
            value={profile.lastName}
            error={errors.lastName}
          />
          {isMentor() ? (
            <Question
              question={{
                label: t('signUpProgramLabel'),
                questionType: 'SingleChoiceDropDown',
                mandatory: true,
                answerOptions: projectsForEmailDomain(profile.projectList, userEmailDomain),
              }}
              onAnswered={handleChange("program")}
              value={profile.program}
              error={errors.program}
            />
          ) : (
            <Question
              question={{
                label: t('signUpInstitutionCodeLabel'),
                questionType: 'Text',
                mandatory: true,
                helpText: t('signUpInstitutionCodeHelpText'),
              }}
              onAnswered={handleChange("institutionCode")}
              value={profile.institutionCode}
              error={errors.institutionCode}
            />
          )}
          <Question
            question={{
              label: t('choosePrimaryLanguage'),
              questionType: 'SingleChoiceDropDown',
              mandatory: true,
              helpText: t('primaryLanguageHelpText'),
              answerOptions: sortBy(
                (supportedLanguages || [])
                  .filter(language => language.isActive)
                  .map(language => ({label: language.language, key: language.languageCode})),
                'key'
              ),
            }}
            onAnswered={handleChange("primaryLanguage")}
            value={profile.primaryLanguage}
            error={errors.primaryLanguage}
          />
          <Question
            question={{
              label: t('signUpPhoneNumberLabel'),
              questionType: 'PhoneNumber',
              autocomplete: 'tel',
              mandatory: true,
            }}
            onAnswered={handleChange("phoneNumber")}
            value={profile.phoneNumber}
            error={errors.phoneNumber}
          />
          {profile.signupError && (
            <p className={classes.error}>{readableError(profile.signupErrorMessage)}</p>
          )}
        </div>
      ),
    },
  ];

  const step = steps[currentIndex];

  const goBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      history.push('/signin');
    }
  };

  return (
    <div className={classes.page}>
      <form
        className={classes.form}
        onSubmit={event => {
          event.preventDefault();
          if (!step.disabled) step.onContinue();
        }}
      >
      <div className={classes.topBar}>
        <div className={classes.container}>
          <div className={classes.column}>
            <p className={classes.eyebrow}>
              {t('stepProgress', {current: currentIndex + 1, total: steps.length})}
            </p>
            <div className={classes.titleRow}>
              <button
                type={'button'}
                className={classes.back}
                onClick={goBack}
                aria-label={`${t('previous')}: ${step.title}`}
              >
                <span className={classes.backArrow} aria-hidden={true}>{'←'}</span>
                {step.title}
              </button>
            </div>
            <div
              className={classes.progressTrack}
              role={'progressbar'}
              aria-label={t('signUp')}
              aria-valuenow={currentIndex + 1}
              aria-valuemin={1}
              aria-valuemax={steps.length}
            >
              <span
                className={classes.progressFill}
                style={{width: `${((currentIndex + 1) * 100) / steps.length}%`}}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.scroller} ref={scrollerRef}>
        <div className={classes.container}>
          <div className={classNames(classes.column, classes.content)}>{step.content}</div>
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.container}>
          <div className={classNames(classes.column, classes.footerInner)}>
            <span className={classes.spacer}/>
            <CtaButton
              type={'submit'}
              size={'dense'}
              label={step.cta}
              disabled={Boolean(step.disabled)}
              loading={busy}
            />
          </div>
        </div>
      </div>
      </form>
    </div>
  );
};

export default Signup;
